/**
 * Point Cloud Inference Plugin
 *
 * Image -> colored metric point cloud (.ply) via the depth service (MoGe-2).
 */

import type { SceneObject, TraceScene } from 'trace-viewer/scene';
import { INFERENCE_API_URL, getImageBlob, imageBlobExtension, createObjectFromInference, inferencePost, POINTCLOUD_SCALE_CORRECTION, type InferenceStatusCallback } from './shared';
import { declareInferencePlugin } from '../plugins/inference-adapter';

async function run(sourceObject: SceneObject, traceScene: TraceScene, onStatus?: InferenceStatusCallback): Promise<SceneObject> {
    const imageBlob = await getImageBlob(sourceObject, traceScene);
    const imgExt = imageBlobExtension(imageBlob);

    const response = await inferencePost('depth', () => {
        const formData = new FormData();
        formData.append('image', imageBlob, `image.${imgExt}`);
        formData.append('format', 'ply');
        return [`${INFERENCE_API_URL}/depth/predict`, { method: 'POST', body: formData }];
    }, undefined, onStatus);
    if (!response.ok) {
        const errText = await response.text();
        throw new Error(`Depth API error (${response.status}): ${errText}`);
    }

    const plyBlob = await response.blob();
    const assetPath = await traceScene.resources.addAssetWithDedup(
        `assets/${sourceObject.name}.ply`,
        plyBlob,
    );

    return createObjectFromInference(
        traceScene, sourceObject, assetPath, 'pointcloud', POINTCLOUD_SCALE_CORRECTION,
    );
}

declareInferencePlugin({
    id: 'pointcloud',
    sourceType: 'image',
    menuLabel: 'Make Point Cloud',
    menuOrder: 201,

    toolName: 'generate_pointcloud',

    run,

    slashCommand: {
        name: 'pointcloud',
        description: 'Convert image to point cloud (depth)',
        usage: '[object]',
        category: 'inference',
        parseArgs: (args, selected) => ({
            object: args || selected || 'selected',
        }),
    },
});
